<template>
  <div
    v-if="isImpersonating"
    class="bg-yellow-500 text-white px-4 py-3 shadow-lg sticky top-0 z-50 border-b-4 border-yellow-600"
  >
    <div class="max-w-7xl mx-auto flex items-center justify-between">
      <div class="flex items-center space-x-3">
        <ExclamationTriangleIcon class="h-6 w-6 flex-shrink-0" />
        <div>
          <p class="font-bold text-sm">
            ⚠️ IMPERSONATION MODE ACTIVE
          </p>
          <p class="text-xs">
            You are viewing <span class="font-semibold">{{ impersonationData.company.name }}</span> as
            <span class="font-semibold">{{ impersonationData.impersonated_user.name }}</span>
          </p>
        </div>
      </div>

      <div class="flex items-center space-x-4">
        <div class="text-xs text-yellow-100">
          <ClockIcon class="h-4 w-4 inline mr-1" />
          {{ duration }}
        </div>

        <form @submit.prevent="stopImpersonating">
          <button
            type="submit"
            :disabled="isSubmitting"
            class="bg-white text-yellow-600 px-4 py-2 rounded font-semibold text-sm hover:bg-yellow-50 transition disabled:opacity-50"
          >
            {{ isSubmitting ? 'Stopping...' : 'Stop Impersonating' }}
          </button>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { router } from '@inertiajs/vue3';
import { ExclamationTriangleIcon, ClockIcon } from '@heroicons/vue/24/outline';

const props = defineProps({
  impersonationData: {
    type: Object,
    default: null,
  },
});

const isImpersonating = computed(() => props.impersonationData !== null);
const isSubmitting = ref(false);

const currentTime = ref(Date.now());
let intervalId = null;

const duration = computed(() => {
  if (!props.impersonationData) return '';

  const startTime = new Date(props.impersonationData.started_at).getTime();
  const diff = currentTime.value - startTime;

  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  }
  return `${minutes}m`;
});

onMounted(() => {
  // Update time every minute
  intervalId = setInterval(() => {
    currentTime.value = Date.now();
  }, 60000);
});

onUnmounted(() => {
  if (intervalId) {
    clearInterval(intervalId);
  }
});

const stopImpersonating = () => {
  isSubmitting.value = true;

  router.post(route('site-owner.companies.stop-impersonating'), {}, {
    onSuccess: () => {
      // Will redirect to site owner dashboard
    },
    onError: () => {
      isSubmitting.value = false;
    },
    onFinish: () => {
      // Keep isSubmitting true on success to prevent double-clicks during redirect
    },
  });
};
</script>
